/* The selected venue on mobile, as the sheet's detail view.
 *
 * Replaces `venue-card` below the breakpoint (ADR 0012 decision 8). The card is
 * not shrunk to fit — it is retired here, because a 240px card floating over a
 * 360px map *under* a sheet is a second surface answering the sheet's question.
 * `venue-card` survives unchanged on desktop, where it floats over 880px of map
 * and nothing else is competing for the venue.
 *
 * Deliberately NOT a list of the venue's items. Watch opens the playlist and
 * Detail owns the rest, which is what it is for — so `×N` is annotation exactly
 * as Index's is. That also leaves this view with nothing scrollable, which is
 * what lets g-sheet treat a swipe anywhere on it as "close".
 *
 * Rebuilt as a detail card in ADR 0014: thumbnail beside an identity block, the
 * visited state as a pill, three one-line actions, then the address and Remove.
 *
 * Nothing here flexes and nothing is budgeted against the viewport — the sheet
 * takes ITS height from this content (`detents.detail = 'content'`), not the
 * other way round, so every block states a real size and the sheet follows. The
 * pill's row is reserved whether or not it shows, so toggling Visited cannot
 * resize the sheet under the finger that toggled it.
 *
 * Presentational: it emits and the workspace acts. `openInGoogleMaps` and the
 * clipboard are the exceptions, as in venue-card — neither leaves anything for
 * the workspace to do.
 */

import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { shadowReset, splitBar, suppressCallout } from '../../design/base.js';
import { apiUrl } from '../../core/api.js';
import type { Venue } from '../../core/types.js';
import { kindMeta, localityOf } from './kinds.js';
import '../../component/g-icon/g-icon.js';
import '../../component/g-button/g-button.js';

@customElement('place-detail')
export class PlaceDetail extends LitElement {
  static override styles = [
    shadowReset,
    splitBar,
    css`
      /* The host owns the horizontal inset for every block, so splitBar's own
         row-inset margin is zeroed below rather than repeated four times.
         height:auto is load-bearing: g-sheet measures this element to size the
         detent, so a percentage height here would measure the screen.
         The bottom pad carries the safe-area inset, since the sheet's cover ends
         at the screen edge. */
      :host {
        display: flex;
        flex-direction: column;
        height: auto;
        padding: var(--space-3) calc(var(--row-inset-r) + var(--space-4))
          max(var(--space-4), var(--safe-b)) calc(var(--row-inset-l) + var(--space-4));
      }

      /* 96x128 (3:4), chosen rather than derived. The identity column beside it
         tops out at ~96px — a 2-line name, the sub-line and the reserved pill
         row — so the thumbnail governs this row's height on every device and
         reserving the pill costs nothing. */
      .top {
        display: flex;
        flex: none;
        gap: var(--space-3);
      }
      .thumb {
        flex: none;
        width: 96px;
        height: 128px;
        display: grid;
        place-items: center;
        overflow: hidden;
        border-radius: var(--radius-md);
        background: var(--surface-2);
      }
      .thumb img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        -webkit-touch-callout: none;
        -webkit-user-drag: none;
        user-select: none;
      }
      /* ~4% of the library has no thumbnail. The kind glyph rather than a hole:
         it holds the box AND reinforces the hue tying this view to its pin. */
      .thumb g-icon {
        color: var(--kc);
        opacity: 0.5;
        --icon-size: 40px;
      }

      .id {
        display: flex;
        flex-direction: column;
        justify-content: center;
        flex: 1;
        min-width: 0;
      }
      .name {
        display: flex;
        align-items: baseline;
        gap: var(--space-2);
        font-size: 1.05rem;
        font-weight: 700;
        line-height: 1.3;
      }
      /* The clamp goes on the span, not .name — a -webkit-box container cannot
         also be the flex row that holds the ×N annotation beside it. */
      .nm {
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
        overflow-wrap: anywhere;
      }
      /* Annotation, not a control — the same call Index's ×N makes, and the
         reason this one no longer has to clear 44px. */
      .n {
        flex: none;
        color: var(--text-dim);
        font-family: var(--font-mono);
        font-size: 0.75rem;
        font-weight: 400;
      }
      .sub {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        margin-top: 2px;
        color: var(--text-dim);
        font-size: 0.82rem;
      }
      .sub .kico {
        flex: none;
        color: var(--kc);
        --icon-size: 15px;
      }
      .locality {
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
      }
      /* The row is reserved whether or not the pill is in it: the sheet's height
         comes from this content, so a pill that appeared on toggle would resize
         the sheet under the finger. Free anyway — the column is shorter than the
         thumbnail either way. */
      .pillrow {
        min-height: 26px;
        margin-top: 6px;
      }
      /* A readout, not a control — ADR 0013's "a capsule means tappable" is a
         rule about the player's scrim, not about sheet content. */
      .pill {
        display: inline-flex;
        align-items: center;
        gap: var(--space-1);
        padding: 3px var(--space-2);
        border-radius: var(--radius-full);
        background: color-mix(in oklab, var(--k-stay) 12%, transparent);
        color: var(--k-stay);
        font-size: 0.82rem;
        --icon-size: 14px;
      }

      /* splitBar is two columns; this is two or three depending on whether the
         venue geocoded, so the count comes from the content rather than a rule
         that would have to know. The host owns the horizontal inset, so the
         fragment's own margin is replaced outright. */
      .split {
        flex: none;
        grid-template-columns: none;
        grid-auto-flow: column;
        grid-auto-columns: 1fr;
        margin: var(--space-3) 0 0;
      }
      /* Stacked rather than beside: at ~99px per segment on a 360px screen an
         icon and a label side by side is cramped. One line each — "Mark visited"
         needs ~75px against 69px of tile at 320px, which is why the visited label
         is one word in both states and leans on colour + aria-pressed. */
      .act {
        flex-direction: column;
        gap: var(--space-1);
        min-height: 52px;
        font-size: 0.75rem;
        white-space: nowrap;
        --icon-size: 20px;
      }
      .act.on {
        color: var(--k-stay);
      }

      /* Two blocks, not two rows: inset, rounded and unfilled is ADR 0011's
         "this is always here" shape. No divider between them — a rule earns its
         place when rows are numerous and alike, and two unlike blocks are
         neither, so the gap does it. */
      .group {
        display: flex;
        flex: none;
        flex-direction: column;
        gap: var(--space-2);
        margin-top: var(--space-3);
      }
      .block {
        display: flex;
        align-items: center;
        gap: var(--space-3);
        width: 100%;
        min-height: var(--touch-min);
        padding: var(--space-2) var(--space-3);
        border: 1px solid var(--border);
        border-radius: var(--radius-md);
        background: none;
        color: var(--text);
        font: inherit;
        font-family: var(--font-ui);
        text-align: left;
        --icon-size: 18px;
      }
      .block .bico {
        flex: none;
        color: var(--text-dim);
      }
      .addr .atext {
        flex: 1;
        min-width: 0;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
        font-size: 0.82rem;
        line-height: 1.4;
      }
      .addr g-button {
        flex: none;
      }
      /* Plain text rather than g-notice, which was the first choice: tone="dim"
         renders no icon (only warn/danger do), so the block lost the left rhythm
         its neighbour gets from the pin. Two blocks in the same slot have to
         start their text on the same column. */
      .offmap .atext {
        flex: 1;
        min-width: 0;
        font-size: 0.82rem;
        line-height: 1.4;
        color: var(--text-dim);
      }

      /* Last, and muted at rest. ADR 0012's fix for this action was distance
         from Watch, not emphasis — a permanently red card would undo it — so
         danger arrives only under the finger, as splitBar's .act.del does.
         The chevron stays: it does lead somewhere (g-map's confirm dialog), so
         it is not the fake navigational row ADR 0011 rejected. */
      .rm {
        cursor: pointer;
        transition: background-color 120ms ease, color 120ms ease;
      }
      .rm .rtext {
        flex: 1;
        min-width: 0;
      }
      .rm .rl {
        display: block;
        font-size: 0.95rem;
      }
      .rm .chev {
        flex: none;
        rotate: -90deg;
      }
      .rm:active {
        color: var(--danger);
        background: color-mix(in oklab, var(--danger) 12%, transparent);
      }
      @media (hover: hover) {
        .rm:hover {
          color: var(--danger);
        }
      }
      .rm:focus-visible {
        outline: none;
        box-shadow: var(--ring);
      }

      /* One trim, for the tail case only: g-sheet clamps a content-sized detent
         at bounds.max, and on a very short viewport the content is taller than
         that. The address is the only unbounded block, so it is the one that
         gives — see the clamp note in ADR 0014. */
      @media (max-height: 620px) {
        .thumb {
          height: 96px;
        }
      }
    `,
  ];

  @property({ attribute: false }) venue!: Venue;

  @state() private copied = false;

  private emit(type: 'watch' | 'toggle-visited' | 'remove'): void {
    this.dispatchEvent(new CustomEvent(type, { bubbles: true, composed: true }));
  }

  private openInGoogleMaps(): void {
    const v = this.venue;
    const url =
      `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${v.lat},${v.lng}`)}` +
      (v.place_id ? `&query_place_id=${encodeURIComponent(v.place_id)}` : '');
    window.open(url, '_blank', 'noopener');
  }

  /* Copies the whole address, not the two lines on screen — the clamp is a
     display concern and a truncated address on the clipboard is useless.
     Third hand-written copy of this pattern (settings-webhook, share-link);
     extraction is deferred on ADR 0011's reveal-tray reasoning, and the next
     component that wants a clipboard button is the trigger. */
  private async copyAddress(): Promise<void> {
    try {
      await navigator.clipboard.writeText(this.venue.formatted_address);
      this.copied = true;
      setTimeout(() => (this.copied = false), 1600);
    } catch {
      // Clipboard can be blocked (permissions, insecure context). The address is
      // selectable text either way, so there is nothing to fall back to.
    }
  }

  override render() {
    const v = this.venue;
    if (!v) return nothing;
    const meta = kindMeta(v.kind, v.geocoded);
    const thumb = v.reels[0]?.thumb;
    const locality = localityOf(v);
    const n = v.reels.length;
    return html`
      <div class="top" style="--kc: ${meta.color}">
        <div class="thumb" @contextmenu=${suppressCallout}>
          ${thumb
            ? html`<img src=${apiUrl(thumb)} alt="" loading="lazy" />`
            : html`<g-icon name=${meta.icon}></g-icon>`}
        </div>
        <div class="id">
          <div class="name">
            <span class="nm">${v.name}</span>
            ${n > 1 ? html`<span class="n">×${n}</span>` : nothing}
          </div>
          <div class="sub">
            <g-icon class="kico" name=${meta.icon}></g-icon>
            ${locality ? html`<span class="locality">${locality}</span>` : nothing}
          </div>
          <div class="pillrow">
            ${v.visited
              ? html`<span class="pill"><g-icon name="check"></g-icon>You’ve been here</span>`
              : nothing}
          </div>
        </div>
      </div>

      <div class="split">
        <button class="act" @click=${() => this.emit('watch')}>
          <g-icon name="play"></g-icon><span>Watch</span>
        </button>
        <button
          class=${v.visited ? 'act on' : 'act'}
          aria-pressed=${v.visited ? 'true' : 'false'}
          @click=${() => this.emit('toggle-visited')}
        >
          <g-icon name="check"></g-icon><span>Visited</span>
        </button>
        ${v.geocoded
          ? html`<button class="act" @click=${this.openInGoogleMaps}>
              <g-icon name="external"></g-icon><span>Maps</span>
            </button>`
          : nothing}
      </div>

      <div class="group">
        ${v.geocoded && v.formatted_address
          ? html`<div class="block addr">
              <g-icon class="bico" name="pin"></g-icon>
              <span class="atext">${v.formatted_address}</span>
              <g-button
                icon-only
                label=${this.copied ? 'Copied' : 'Copy address'}
                @click=${this.copyAddress}
              >
                <g-icon name=${this.copied ? 'check' : 'copy'}></g-icon>
              </g-button>
            </div>`
          : html`<div class="block offmap">
              <g-icon class="bico" name="ban"></g-icon>
              <span class="atext">No coordinates for this one, so it isn’t on the map.</span>
            </div>`}
        <button class="block rm" @click=${() => this.emit('remove')}>
          <g-icon class="bico" name="trash"></g-icon>
          <span class="rtext">
            <span class="rl">Remove from map</span>
          </span>
          <g-icon class="chev" name="chev-down"></g-icon>
        </button>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'place-detail': PlaceDetail;
  }
}
